import { z } from 'zod';
/**
 * Giving a verified identity an organisation to work in.
 *
 * No address and no password: the identity provider holds both, and the
 * request arrives carrying a token it issued. The only thing left for this
 * form to ask is what the organisation is called.
 *
 * Two values below outlived the account creation they were written for.
 * `emailSchema` is how every address in the product is read — contacts,
 * invitations, bounce reports — and `PASSWORD_MIN` is still the floor for
 * operator accounts, which keep their own sign-in (ADR 0004).
 */
/**
 * Deliberately permissive about shape, strict about length.
 *
 * Every clever email regex rejects addresses that work. The only test that
 * settles whether an address is real is sending to it and having someone read
 * it, which is what the identity provider's verification does — so the check
 * here only has to stop the obviously malformed.
 */
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(254, 'an email address is at most 254 characters')
    .regex(/^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/, 'that does not look like an email address');
/**
 * Length is the requirement. Composition rules are not.
 *
 * No character-class rules, because "one uppercase, one digit, one symbol"
 * reliably produces `Password1!` — memorable to nobody and guessable by
 * everyone. The upper bound exists because the hash is computed over the whole
 * input and an unbounded one is a denial-of-service vector, not because longer
 * is worse.
 *
 * **Eight, set deliberately on 20 Oct 2026**, replacing the twelve NIST
 * 800-63B recommends. Recorded here rather than left as a bare number so the
 * next person to read it knows it was decided rather than drifted into: eight
 * is the floor most of the industry still uses, and it is not the floor that
 * resists an offline guess against a leaked hash, which is what the longer
 * minimum was protecting against.
 *
 * **It governs operator accounts only.** Customer passwords are the identity
 * provider's, held under its own rules — which were set to match this one so
 * the two halves of the product do not disagree about what a password is.
 */
export const PASSWORD_MIN = 8;
export const signUpRequestSchema = z.object({
    organizationName: z.string().trim().min(1, 'your organisation needs a name').max(120),
});
/**
 * Every outcome is reported plainly, which the open form this replaces could
 * not do.
 *
 * That one answered identically whether or not an address was registered,
 * because anyone could ask it and "already registered" would have made it a
 * way to find out who has an account here. This one cannot be reached without
 * a verified token, and tells its caller only about themselves.
 */
export const signUpResponseSchema = z.object({
    status: z.enum([
        'created',
        /** They already have a place here. Re-submitting the form is not an error. */
        'already-provisioned',
        /** Their address belongs to an account made some other way. */
        'address-taken',
        /** The provider has not confirmed the address. */
        'unverified-address',
    ]),
});
//# sourceMappingURL=signUp.js.map